import { Typography } from 'components';
import { useTranslations } from 'next-intl';
import { FC, HTMLAttributes, PropsWithChildren } from 'react';
import CheckBallIcon from './CheckBallIcon';

interface IPasswordValidator extends HTMLAttributes<HTMLDivElement> {
  password: string;
}

const PasswordValidator: FC<PropsWithChildren<IPasswordValidator>> = ({ password, ...rest }) => {
  const t = useTranslations();

  function validationUpperLower(password?: string) {
    if (password === undefined || password === '') return 'inactive';

    // Check for at least one uppercase letter and one lowercase letter
    if (!/(?=.*[a-z])(?=.*[A-Z])/.test(password)) return 'error';

    return 'active';
  }

  function validationContainNumberSymbol(password?: string) {
    if (password === undefined || password === '') return 'inactive';

    // Check for at least one letter
    if (!/(?=.*[a-zA-Z])/.test(password)) return 'error';

    // Check for at least one number
    if (!/(?=.*\d)/.test(password)) return 'error';

    // Check for at least one symbol
    if (!/(?=.*[!@#$%^&*])/.test(password)) return 'error';

    return 'active';
  }

  function validationEightChars(password?: string) {
    if (password === undefined || password === '') return 'inactive';

    // Check if the password is at least 8 characters long
    if (password.length < 8) return 'error';

    return 'active';
  }

  const passwordValidationUpperLower = validationUpperLower(password);
  const passwordContainNumberSymbol = validationContainNumberSymbol(password);
  const passwordEightChars = validationEightChars(password);

  return (
    <div
      className="my-2 inline-flex h-14 w-full flex-col items-start justify-start gap-0.5"
      {...rest}
    >
      <div className="inline-flex items-center justify-start gap-1">
        <div className="relative h-4 w-4 bg-white">
          <div className="absolute left-0 top-0 h-4 w-4" />
          <CheckBallIcon status={passwordValidationUpperLower} />
        </div>
        <Typography variant="regular5" className="text-tertiary-300 !delay-0 ">
          {t('components.password_validator.upper_lower')}
        </Typography>
      </div>
      <div className="inline-flex items-center justify-start gap-1">
        <div className="relative h-4 w-4 bg-white">
          <div className="absolute left-0 top-0 h-4 w-4" />
          <CheckBallIcon status={passwordContainNumberSymbol} />
        </div>
        <Typography variant="regular5" className="text-tertiary-300 !delay-0 ">
          {t('components.password_validator.contain_number_symbol')}
        </Typography>
      </div>
      <div className="inline-flex items-center justify-start gap-1">
        <div className="relative h-4 w-4 bg-white">
          <div className="absolute left-0 top-0 h-4 w-4" />
          <CheckBallIcon status={passwordEightChars} />
        </div>
        <Typography variant="regular5" className="text-tertiary-300 !delay-0 ">
          {t('components.password_validator.eight_chars')}
        </Typography>
      </div>
    </div>
  );
};

export default PasswordValidator;
